import React, { useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import OktaAuth from "@okta/okta-auth-js";
import getOktaInstance from "../../auth/oktaConfig";
import { resolveCachedEntraAccount } from "../../auth/entraAccount";
import Button from "./Button";
import {
  Menu,
  MenuItem,
  MenuTrigger,
  MenuPopover,
  MenuList,
  Button as FluentButton,
  makeStyles,
} from "@fluentui/react-components";
import { ChevronDown12Regular } from "@fluentui/react-icons";
import { ErrorMessages } from "../../constants/messages";
import {
  clearExplicitSignOut,
  getEntraLoginHint,
  getLoginMethod,
  getOktaLoginHint,
  isExplicitlySignedOut,
  rememberEntraLoginHint,
  rememberLoginMethod,
  rememberOktaLoginHint,
} from "../../auth/loginPreferences";

const oktaScopes = ["openid", "profile", "email"];

// Okta's 120s default is wrong at both ends: too slow for a silent attempt the user is waiting on,
// and too fast for a sign-in where they may be enrolling in Okta Verify.
const OKTA_SILENT_TIMEOUT_MS = 10000;
const OKTA_POPUP_TIMEOUT_MS = 300000;

/** Never the error object itself - SDK errors can carry the underlying response. */
const describeAuthError = (error: any): string =>
  error?.errorCode || error?.name || error?.message || "unknown error";

const isPopupBlocked = (error: any): boolean => String(error).includes("popup");

/** They closed the popup themselves; no need to report it back to them. */
const isUserCancelled = (error: any): boolean =>
  error?.errorCode === "user_cancelled" || String(error).toLowerCase().includes("cancel");

/** Cached Okta tokens that Okta still accepts, else null. Loosely typed - `Tokens` is not exported. */
const readVerifiedStoredOktaTokens = async (oktaInstance: OktaAuth): Promise<any> => {
  try {
    const tokens = await oktaInstance.tokenManager.getTokens();
    if (!tokens?.idToken || !tokens?.accessToken) {
      return null;
    }
    if (
      oktaInstance.tokenManager.hasExpired(tokens.idToken) ||
      oktaInstance.tokenManager.hasExpired(tokens.accessToken)
    ) {
      return null;
    }
    // Expiry alone would let a revoked pair through. /userinfo asks Okta, and authenticates with the
    // access token rather than a cookie, so the pane's cookie policy cannot skew the answer.
    await oktaInstance.token.getUserInfo(tokens.accessToken, tokens.idToken);
    return tokens;
  } catch (error) {
    console.log("Stored Okta tokens are not usable, renewing instead:", describeAuthError(error));
    return null;
  }
};

interface LoginButtonInterface {
  isInitialized: boolean;
  setIsInitialized: (isInitialized: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  userChanged: (authToken: string, ID: string, oktaIdToken: string, name: string, identityProvider: string) => void;
  errorRaised: (message: string) => void;
}

const useStyles = makeStyles({
  dropDownButton: {
    borderRadius: "6px",
    border: "0.25pt solid #C4E1E0",
    backgroundColor: "#ffffff",
    color: "#001C71",
    fontSize: "11px",
    display: "block",
    "&:hover": {
      border: "0.25pt solid #7FBDBA",
      backgroundColor: "#ffffff",
      color: "#001C71",
    },
    "&:disabled": {
      border: "0.25pt solid #E0E0E0",
      backgroundColor: "#F5F5F5",
      color: "#A0A0A0",
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
});

const LoginButton: React.FC<LoginButtonInterface> = ({
  isInitialized,
  setIsInitialized,
  setIsLoading,
  userChanged,
  errorRaised,
}) => {
  const { instance } = useMsal();
  window["authInstance"] = instance;
  window["oktaInstance"] = getOktaInstance();
  
  const styles = useStyles();

  const handleLogin = async () => {
    setIsLoading(true);
    const scopes = ["openid", "profile", "email"];
    const loginCompleted = (response: any) => {
      let account = response.account;
      userChanged(response.idToken, response.idToken, account.username, account.name, "Entra");
      instance.setActiveAccount(account);
      console.log("Login successful.");
      rememberLoginMethod("microsoft");
      rememberEntraLoginHint(account.username);
      clearExplicitSignOut();
    };
    try {
      // Silent first. A hint-less `ssoSilent` throws when two IdP sessions are live, which is what
      // surfaced as a prompt on every return, so it is the last silent resort and never the first.
      const cachedAccount = resolveCachedEntraAccount(instance);
      const loginHint = cachedAccount?.username || getEntraLoginHint();
      let silentSucceeded = false;
      try {
        if (cachedAccount) {
          // Also re-establishes the active account that api.ts needs after a reload.
          instance.setActiveAccount(cachedAccount);
          await instance.acquireTokenSilent({ scopes, account: cachedAccount }).then(loginCompleted);
        } else {
          await instance.ssoSilent({ scopes, loginHint }).then(loginCompleted);
        }
        silentSucceeded = true;
      } catch (silentError) {
        console.log("Silent Entra sign-in unavailable, prompting instead:", describeAuthError(silentError));
      }
      if (!silentSucceeded) {
        await instance.loginPopup({ scopes, loginHint }).then(loginCompleted);
      }
    } catch (error) {
      console.error("Login failed:", error);
      if (!isUserCancelled(error)) {
        errorRaised(isPopupBlocked(error) ? ErrorMessages.PopupBlocked : ErrorMessages.SignInFailed);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginWithOkta = async () => {
    setIsLoading(true);

    try {
      const oktaInstance = getOktaInstance();

      const oktaLoginCompleted = (tokens: any) => {
        const idToken = tokens.idToken?.idToken;
        const userEmail: string = tokens.idToken?.claims.email || "";
        const userName: string = tokens.idToken?.claims.name || "";
        userChanged(idToken, idToken, userEmail, userName, "Okta");
        console.log("Okta Login successful.");
        rememberLoginMethod("okta");
        rememberOktaLoginHint(tokens.idToken?.claims.preferred_username || userEmail);
        clearExplicitSignOut();
      };

      // Same silent-first ladder as Entra. The popup is where this flow used to start, which is why
      // Okta users were prompted on every return.
      const storedTokens = await readVerifiedStoredOktaTokens(oktaInstance);
      if (storedTokens) {
        oktaLoginCompleted(storedTokens);
        return;
      }

      // Without the hint a renewal follows whichever org session Okta treats as active.
      const oktaLoginHint = getOktaLoginHint();
      let tokens: any = null;
      try {
        const silentResponse = await oktaInstance.token.getWithoutPrompt({
          responseType: ["token", "id_token"],
          scopes: oktaScopes,
          loginHint: oktaLoginHint,
          timeout: OKTA_SILENT_TIMEOUT_MS,
        });
        tokens = silentResponse.tokens;
      } catch (silentError) {
        console.log("Silent Okta sign-in unavailable, prompting instead:", describeAuthError(silentError));
      }
      if (!tokens) {
        const popupResponse = await oktaInstance.token.getWithPopup({
          responseType: ["token", "id_token"],
          scopes: oktaScopes,
          loginHint: oktaLoginHint,
          timeout: OKTA_POPUP_TIMEOUT_MS,
        });
        tokens = popupResponse.tokens;
      }

      oktaInstance.tokenManager.add("idToken", tokens.idToken);
      oktaInstance.tokenManager.add("accessToken", tokens.accessToken);
      oktaLoginCompleted(tokens);
    } catch (error) {
      console.error("Okta Login failed:", error);
      if (!isUserCancelled(error)) {
        errorRaised(isPopupBlocked(error) ? ErrorMessages.PopupBlocked : ErrorMessages.SignInFailed);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginWithLastOption = async () => {
    // An explicit click always signs in; no remembered provider means Microsoft.
    if (getLoginMethod() === "okta") {
      await handleLoginWithOkta();
    } else {
      await handleLogin();
    }
  };

  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      setTimeout(() => {
        // Never guess a provider here - it used to prompt Microsoft at first-time and Okta users.
        const loginMethod = getLoginMethod();
        if (!loginMethod) {
          console.log("No previous sign-in on record - waiting for the user to choose a provider");
          return;
        }
        if (isExplicitlySignedOut()) {
          console.log("User signed out deliberately - waiting for them to sign in again");
          return;
        }
        setIsLoading(true);
        if (loginMethod === "okta") {
          setTimeout(handleLoginWithOkta);
          console.log("Automatic login with Okta");
        } else {
          setTimeout(handleLogin);
          console.log("Automatic login with Microsoft");
        }
      });
    }
  });

  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      <Button onClick={handleLoginWithLastOption} label="Sign in" />;
      <Menu>
        <MenuTrigger>
          <FluentButton
            icon={<ChevronDown12Regular />}
            size="small"
            className={styles.dropDownButton}
            style={{ padding: "1px", margin: "0px", marginLeft: "-8px" }}
          />
        </MenuTrigger>
        <MenuPopover>
          <MenuList style={{ marginTop: "8px" }}>
            <MenuItem onClick={handleLogin}>with Microsoft</MenuItem>
            <MenuItem onClick={handleLoginWithOkta}>with Okta</MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    </span>
  );
};

export default LoginButton;
